/* ============================================================
   ZNAKI · intro.js
   Warstwa DOM nad tablicą: ekran wprowadzający, stopka instalacji
   i nakładka "info".

   Scenariusz (czasy w PARAMS.intro):
     1. tekst → animowana dłoń (SVG) → przycisk "rozpocznij"
     2. kliknięcie: przycisk gaśnie, typografia wyjeżdża w górę,
        a tablica rusza — figury wjeżdżają zza sceny (index.js)
     3. po chwili odsłania się stopka z datami i przyciskami

   Warstwa jest przezroczysta dla wskaźnika (pointer-events w SCSS),
   więc gest scrolla dociera do tablicy również w trakcie wyjazdu
   typografii — instalacja odpowiada na niego natychmiast.

   Widoki: views/__2026/editionhome/editionhome_playground_{intro,footer,info,instr}.php
   ============================================================ */

import gsap from 'gsap';
import { PARAMS } from './config';

const SEL = {
  intro:     '.playground-intro',
  text:      '.playground-intro-text',
  instr:     '.playground-intro-instr',
  cta:       '.playground-intro-cta',
  start:     '.playground-start',
  footer:    '.playground-footer',
  footerRow: '.playground-footer .line > .inner',
  info:      '.playground-info',
  /* uwaga: klasa stanu na <html> to playground-info-open — przycisk musi
     nazywać się inaczej, inaczej closest() złapałby go przy każdym kliknięciu */
  infoOpen:  '.playground-info-expand',
  infoClose: '.playground-info-close',
  infoRow:   '.pi-row-1, .pi-row-2, .pi-row-3',
};

const INFO_CLASS = 'playground-info-open';

class Intro {

  /**
   * @param {Element} scope — kontener widoku (.editionhome-full)
   * @param {object} hooks — { onStart() } wywoływane po kliknięciu "rozpocznij"
   */
  constructor(scope, hooks) {
    this.scope = scope || document;
    this.hooks = hooks || {};

    this.el = this.scope.querySelector(SEL.intro);
    this.footerEl = this.scope.querySelector(SEL.footer);
    this.infoEl = this.scope.querySelector(SEL.info);
    this.btn = this.el && this.el.querySelector(SEL.start);

    this.started = false;
    this._calls = [];      /* gsap.delayedCall — do posprzątania */
    this._loops = [];      /* pętle podpowiedzi — j.w. */

    this._onClickBound = (e) => this._onClick(e);
    this._onKeyBound = (e) => this._onKey(e);
    document.addEventListener('click', this._onClickBound);
    document.addEventListener('keydown', this._onKeyBound);

    this._prepFooter();

    if (this.el) this._revealIntro();
    else this._revealFooter();   /* bez ekranu wprowadzającego stopka wchodzi od razu */
  }

  /** @return {Boolean} czy ekran wprowadzający w ogóle jest na stronie */
  get active() {
    return !!this.el;
  }

  destroy() {
    document.removeEventListener('click', this._onClickBound);
    document.removeEventListener('keydown', this._onKeyBound);
    this._calls.forEach((c) => c.kill());
    this._loops.forEach((t) => t.kill());
    this._calls.length = 0;
    this._loops.length = 0;
    if (this.el) {
      gsap.killTweensOf(this.el);
      gsap.killTweensOf(this.el.querySelectorAll('*'));
    }
    if (this.footerEl) gsap.killTweensOf(this.footerEl.querySelectorAll('*'));
    if (this.infoEl) gsap.killTweensOf(this.infoEl.querySelectorAll('*'));
    document.documentElement.classList.remove(INFO_CLASS);
    this.el = this.footerEl = this.infoEl = this.btn = null;
  }

  /* --- ekran wprowadzający ------------------------------------ */

  _revealIntro() {
    const I = PARAMS.intro;
    const rows = [
      [this.el.querySelector(SEL.text),  I.textDelay],
      [this.el.querySelector(SEL.instr), I.textDelay + I.instrDelay],
      [this.el.querySelector(SEL.cta),   I.textDelay + I.instrDelay + I.ctaDelay],
    ];
    gsap.set(this.el, { visibility: 'visible', opacity: 1, yPercent: 0 });
    rows.forEach(([el, delay]) => {
      if (!el) return;
      gsap.set(el, { opacity: 0 });
      gsap.to(el, { opacity: 1, duration: I.fade, delay, ease: 'power2.out' });
    });
    this.el.querySelectorAll(SEL.instr + ' svg').forEach((svg) => this.playInstr(svg));
  }

  _onClick(e) {
    const t = e.target;
    if (!t || !t.closest) return;
    if (t.closest(SEL.start)) { this.start(); return; }
    if (t.closest(SEL.infoOpen)) { this.openInfo(); return; }
    if (t.closest(SEL.infoClose)) { this.closeInfo(); return; }
  }

  _onKey(e) {
    if (e.key === 'Escape') this.closeInfo();
  }

  /* Start instalacji — jedno wejście, bez powrotu: przycisk gaśnie,
     żeby powtórne kliknięcie nie odpaliło sekwencji od nowa. */
  start() {
    if (this.started || !this.el) return;
    this.started = true;

    const I = PARAMS.intro;
    if (this.btn) {
      this.btn.disabled = true;
      this.btn.setAttribute('aria-disabled', 'true');
    }
    this.el.classList.add('is-started');

    /* typografia odjeżdża w górę w tempie wjazdu figur —
       tak, jakby stronę przewinął sam użytkownik */
    gsap.timeline()
      .to(this.el, { yPercent: -100, duration: I.outDur, ease: 'power2.inOut' }, 0)
      .to(this.el, { opacity: 0, duration: I.outDur * 0.55, ease: 'power1.in' }, I.outDur * 0.45)
      .set(this.el, { visibility: 'hidden' })
      .call(() => this._stopInstr(this.el));

    this._calls.push(gsap.delayedCall(
      Math.max(0, I.startDur + I.footerDelay),
      () => this._revealFooter()
    ));

    if (this.hooks.onStart) this.hooks.onStart();
  }

  /* --- stopka instalacji -------------------------------------- */

  _prepFooter() {
    if (!this.footerEl) return;
    gsap.set(this.footerEl, { visibility: 'hidden' });
    gsap.set(this.footerEl.querySelectorAll(SEL.footerRow), { yPercent: 115 });
  }

  _revealFooter() {
    if (!this.footerEl) return;
    gsap.set(this.footerEl, { visibility: 'visible' });
    gsap.to(this.footerEl.querySelectorAll(SEL.footerRow), {
      yPercent: 0,
      duration: PARAMS.intro.footerDur,
      ease: 'power3.out',
      stagger: 0.07,
    });
  }

  /* --- nakładka "info" ---------------------------------------- */

  openInfo() {
    if (!this.infoEl) return;
    const rows = this.infoEl.querySelectorAll(SEL.infoRow);
    rows.forEach((el, i) => {
      gsap.killTweensOf(el);
      gsap.set(el, { opacity: 0 });
      gsap.to(el, { opacity: 1, duration: 0.6, delay: 0.15 + i * 0.06, ease: 'power3.out' });
    });
    this.infoEl.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add(INFO_CLASS);
    this.infoEl.querySelectorAll('.playground-info-instr svg')
      .forEach((svg) => this.playInstr(svg));
  }

  closeInfo() {
    if (!this.infoEl) return;
    if (!document.documentElement.classList.contains(INFO_CLASS)) return;
    this.infoEl.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove(INFO_CLASS);
    this._stopInstr(this.infoEl);
  }

  /* --- animowana podpowiedź (dłoń + krążek) -------------------- */
  /* Rysunek i tempo z edycji 2025 — tam dłoń chodziła w poziomie,
     tu w pionie, bo instalację przewija się w górę i w dół. */

  playInstr(svg) {
    const hand = svg.querySelector('.hand');
    const circle2 = svg.querySelector('.circle-2');
    const arrows = svg.querySelector('.arrows-wrapper');
    if (!hand || !circle2 || !arrows) return;

    gsap.killTweensOf([hand, circle2, arrows]);
    gsap.set(circle2, { scale: 0.5, opacity: 0, transformOrigin: 'center center' });
    gsap.set(arrows, { scale: 0.5, opacity: 0, transformOrigin: 'center center' });
    gsap.set(hand, { y: 0 });

    gsap.to(hand, { y: -22, duration: 1, ease: 'power3.inOut', delay: 1 });
    gsap.to(circle2, { scale: 1, opacity: 1, duration: 1, ease: 'power3.inOut', delay: 1.2 });
    gsap.to(arrows, {
      opacity: 1, scale: 1, duration: 0.5, ease: 'back.inOut', delay: 1.4,
      onComplete: () => this._loopInstr(svg),
    });
  }

  /* podpowiedź poza kadrem nie musi chodzić — zwalniamy pętlę */
  _stopInstr(scope) {
    if (!scope) return;
    scope.querySelectorAll('.installation-instr svg').forEach((svg) => {
      gsap.killTweensOf(svg.querySelectorAll('.hand, .circle-wrapper, .circle-2, .arrows-wrapper'));
      if (svg._wjInstrLoop) {
        svg._wjInstrLoop.kill();
        svg._wjInstrLoop = null;
      }
    });
  }

  _loopInstr(svg) {
    const hand = svg.querySelector('.hand');
    const circle = svg.querySelector('.circle-wrapper');
    const moveHand = 18;
    const moveCircle = 15;

    gsap.killTweensOf([hand, circle]);
    gsap.to(hand, { y: -moveHand, duration: 1, ease: 'power1.inOut' });
    gsap.to(circle, {
      y: -moveCircle, duration: 1.2, delay: 0.2, ease: 'power1.inOut',
      onComplete: () => {
        const t = gsap.timeline({ repeat: -1, yoyo: true });
        t.to(hand, { y: moveHand, duration: 1.5, ease: 'power3.inOut' });
        t.to(circle, { y: moveCircle, duration: 1.5, ease: 'power3.inOut' }, 0);
        svg._wjInstrLoop = t;
        this._loops.push(t);
      },
    });
  }
}

export default Intro;
